import { useEffect } from "react";
import { useSelector } from "react-redux";
import { FaHome, FaExternalLinkAlt, FaSignOutAlt } from "react-icons/fa";
import Layout from "@wrappers/layouts/main-layout/Layout";
import { IAccess, IClientAccess, Platforms, TActiveClientId, TUserdata } from "@/ts/common-types";
import { doLogout } from "@services/UserService";

const Unauthorized = ({ isStagingRestriction = false }: { isStagingRestriction?: boolean }) => {

    const activeClientId: number = useSelector(
        (state: TActiveClientId) => +state.activeClientId.value
    );

    const userData = useSelector((state: TUserdata) => state.userData.value);

    useEffect(() => {
        document.title = isStagingRestriction 
            ? `Staging Access Restricted | REVINITI` 
            : `Unauthorized | REVINITI`;
    }, [isStagingRestriction]);

    const handleHomeClick = () => {
        const userAccess = userData?.access?.find((access: IAccess) =>
            access.clients.find(
                (client: IClientAccess) => client.client_id === activeClientId
            )
        );
        const platformAccess = userAccess ? userAccess?.platforms.map((item: string) => item.toLowerCase()) : [];

        window.location.href = platformAccess.length === 1 && platformAccess.includes(Platforms.PLANNING) ?
            `/${Platforms.PLANNING}/revstack` : "/";
    };

    const handleProductionRedirect = () => {
        window.location.href = process.env.VITE_PROD_URL;
    };

    // If it's a staging restriction, show full-page without layout
    if (isStagingRestriction) {
        return (
            <div className="page-not-found pt-5" style={{ position: 'relative' }}>
                {/* Logout button at top right */}
                <div
                    className="anchor-class user-status"
                    style={{
                        position: 'absolute',
                        top: '20px',
                        right: '20px',
                        zIndex: 1000
                    }}
                    onClick={() => doLogout()}
                    onKeyUp={() => doLogout()}
                >
                    <FaSignOutAlt
                        style={{
                            fontSize: "15px",
                            color: "#5cb85c",
                        }}
                    />
                    <span className="ms-1">Logout</span>
                </div>

                <div className="card bg-light shadow">
                    <h2>401</h2>
                    <h3 className="mt-4">Access Restricted</h3>
                    <h5>
                        Staging access is not enabled for your account.
                        <br />
                        Please contact your POC.
                    </h5>
                    <div className="mt-5">
                        <button
                            onClick={handleProductionRedirect}
                            type="button"
                            className="btn m-2 m-md-0 btn-primary"
                        >
                            <div className="d-flex align-items-center gap-2">
                                Production: {process.env.VITE_PROD_URL || 'https://app.1digitalstack.ai'}
                                <FaExternalLinkAlt />
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Regular unauthorized page with layout
    return (
        <Layout>
            <div className="main-content-body subheader-fixed">
                <div className="page-not-found pt-5">
                    <div className="card bg-light shadow">
                        <h2>401</h2>
                        <h3 className="mt-4">Unauthorized Access</h3>
                        <h5>
                            We're sorry, you don't have permission to access this page.
                            Please make sure you are logged in with the correct credentials.
                            Click the button below to return to the homepage.
                        </h5>
                        <div className="mt-5">
                            <button
                                onClick={handleHomeClick}
                                className="btn m-2 m-md-0 btn-primary"
                            >
                                <div className="d-flex align-items-center gap-1">
                                    <FaHome className="" /> Back Home
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Unauthorized; 